// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Ask a colleague (design/agents-side-bar r2-q1..q4). The owner's side: who a thread's
// questions went to (the question card's "Asked Lerato · waiting", the thread's "Answered by …").
// The asked person's side: the card in the DM, read and answered by ask id. The API is the judge
// of who may see or answer what; this only keeps what it said.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import type {RestResponse} from '@app/features/platform/types/TransportTypes';
import type {AgentAnswers, AgentAsk, AgentAskView} from '@fluxer/schema/src/domains/konvo/Agents';
import {makeAutoObservable, observable, runInAction} from 'mobx';

function answered<R extends RestResponse<unknown>>(response: R): R {
	if (!response.ok) throw new HttpError({method: 'GET', path: '', status: response.status, body: response.body});
	return response;
}

const threadKey = (machineId: string, threadId: string) => `${machineId}\u0000${threadId}`;

type CardState = {view: AgentAskView | null; failed: boolean; loading: boolean};

class AgentAsksStore {
	/** The owner's asks per thread. */
	threads = observable.map<string, Array<AgentAsk>>();
	/** Asks behind DM cards, by ask id. */
	cards = observable.map<string, CardState>();

	constructor() {
		makeAutoObservable(this, {threads: false, cards: false}, {autoBind: true});
	}

	forThread(machineId: string, threadId: string): ReadonlyArray<AgentAsk> {
		return this.threads.get(threadKey(machineId, threadId)) ?? [];
	}

	async loadThread(machineId: string, threadId: string): Promise<void> {
		try {
			const response = answered(
				await http.get<{asks: Array<AgentAsk>}>(Endpoints.KONVO_AGENTS_THREAD_ASKS(machineId, threadId), {
					mode: 'silent',
				}),
			);
			runInAction(() => this.threads.set(threadKey(machineId, threadId), response.body.asks));
		} catch {
			// Not shown until it loads; the next open or update tries again.
		}
	}

	/** A thread's asks changed (KONVO_AGENTS_UPDATE asks): read them again if they are on screen. */
	applyUpdate(machineId: string, threadId: string): void {
		if (this.threads.has(threadKey(machineId, threadId))) void this.loadThread(machineId, threadId);
	}

	async ask(machineId: string, threadId: string, requestId: string, personId: string, note: string): Promise<AgentAsk> {
		const response = await http.post<{ask: AgentAsk}>(
			Endpoints.KONVO_AGENTS_THREAD_QUESTION_ASK(machineId, threadId, requestId),
			{body: {personId, ...(note.trim() ? {note: note.trim()} : {})}},
		);
		const made = response.body.ask;
		runInAction(() => {
			const key = threadKey(machineId, threadId);
			const list = (this.threads.get(key) ?? []).filter((entry) => entry.askId !== made.askId);
			this.threads.set(key, [...list, made]);
		});
		return made;
	}

	card(askId: string): CardState | undefined {
		return this.cards.get(askId);
	}

	/** Reads the ask behind a DM card (again when the card's message changed). */
	async loadCard(askId: string): Promise<void> {
		const current = this.cards.get(askId);
		runInAction(() => this.cards.set(askId, {view: current?.view ?? null, failed: false, loading: true}));
		try {
			const response = answered(await http.get<AgentAskView>(Endpoints.KONVO_AGENTS_ASK(askId), {mode: 'silent'}));
			runInAction(() => this.cards.set(askId, {view: response.body, failed: false, loading: false}));
		} catch {
			runInAction(() => this.cards.set(askId, {view: current?.view ?? null, failed: true, loading: false}));
		}
	}

	/** The asked person answers; the card shows the API's verdict either way. */
	async answer(askId: string, answers: AgentAnswers): Promise<void> {
		const view = this.cards.get(askId)?.view;
		if (!view) return;
		try {
			const response = await http.post<{ask: AgentAsk}>(Endpoints.KONVO_AGENTS_ASK_ANSWER(askId), {
				body: {answers, questionsHash: view.ask.questionsHash},
			});
			runInAction(() =>
				this.cards.set(askId, {view: {...view, ask: response.body.ask}, failed: false, loading: false}),
			);
		} catch (error) {
			void this.loadCard(askId);
			throw error;
		}
	}
}

const AgentAsks = new AgentAsksStore();
export default AgentAsks;
